import { z } from 'zod';
import { paginatedSchema, paginationSchema, uuidSchema } from './common';

/** 결제 상태 — 토스 결제 상태와 같은 이름이고 앞으로만 바뀐다(결제 설계 §8-3). */
export const PAYMENT_STATUSES = [
  'READY',
  'DONE',
  'PARTIAL_CANCELED',
  'CANCELED',
  'ABORTED',
  'EXPIRED',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/** ADMIN 결제 목록의 기본 필터 — 승인되지 않은 시도(READY)를 뺀다(결제 설계 §5). */
export const DEFAULT_ADMIN_PAYMENT_STATUSES: readonly PaymentStatus[] = [
  'DONE',
  'PARTIAL_CANCELED',
  'CANCELED',
  'ABORTED',
  'EXPIRED',
];

/** 결제 수단 제공자 — 토스 카드 결제와 계좌이체(운영자 입금 확인). */
export const PAYMENT_PROVIDERS = ['TOSS', 'BANK_TRANSFER'] as const;
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];
export const paymentProviderSchema = z.enum(PAYMENT_PROVIDERS);

export const PAYMENT_CANCEL_STATUSES = ['PENDING', 'DONE', 'FAILED'] as const;
export type PaymentCancelStatus = (typeof PAYMENT_CANCEL_STATUSES)[number];

/** 주문 ID 길이 상한 — 토스 orderId 규칙(6~64자)과 맞춘다. */
export const MAX_PAYMENT_ORDER_ID_LENGTH = 64;
/** 토스 paymentKey 길이 상한(토스 API 명세 200자). */
export const MAX_PAYMENT_KEY_LENGTH = 200;
/** 환불 사유 상한 — 토스 cancelReason·payment_cancels.reason 과 같다. */
export const MAX_CANCEL_REASON_LENGTH = 200;

/**
 * 우리 주문 ID — 주문(`orders.id`) uuid 다(`payments.order_id` 는 uuid FK). 계약은 토스 orderId 규칙대로 1~64자 문자열로 받고, uuid 가
 * 아니면 주문 쪽 결제 대상 조회가 DB 를 읽지 않고 "주문을 찾을 수 없습니다."로 답한다.
 */
const orderIdSchema = z.string().trim().min(1).max(MAX_PAYMENT_ORDER_ID_LENGTH);
const wonSchema = z.number().int().positive();

// ---------------------------------------------------------------------------
// 회원 표면 (payments.*)
// ---------------------------------------------------------------------------

export const paymentCheckoutInputSchema = z.object({ orderId: orderIdSchema });

export const paymentCheckoutSchema = z.discriminatedUnion('payable', [
  z.object({
    payable: z.literal(true),
    orderId: z.string(),
    amount: z.number().int(),
    orderName: z.string(),
    /** 토스 customerKey — 회원 users.id. */
    customerKey: z.uuid(),
    /** 열린(입금 대기) 계좌이체가 있으면 true — web 이 카드 위젯 대신 입금 안내를 띄운다. */
    openBankTransfer: z.boolean(),
  }),
  z.object({ payable: z.literal(false), reason: z.string() }),
]);
export type PaymentCheckout = z.infer<typeof paymentCheckoutSchema>;

export const preparePaymentInputSchema = z.object({ orderId: orderIdSchema });

export const preparedPaymentSchema = z.object({
  /** 토스 orderId 로 넘길 값 = payments.id. */
  paymentId: z.uuid(),
  amount: z.number().int(),
  orderName: z.string(),
});
export type PreparedPayment = z.infer<typeof preparedPaymentSchema>;

export const confirmPaymentInputSchema = z.object({
  paymentKey: z.string().min(1).max(MAX_PAYMENT_KEY_LENGTH),
  /** 토스 주문번호 = payments.id. */
  orderId: uuidSchema,
  amount: wonSchema,
});
export type ConfirmPaymentInput = z.infer<typeof confirmPaymentInputSchema>;

export const memberPaymentSchema = z.object({
  id: z.uuid(),
  orderId: z.string(),
  status: z.enum(PAYMENT_STATUSES),
  amount: z.number().int(),
  orderName: z.string(),
  method: z.string().nullable(),
  approvedAt: z.iso.datetime().nullable(),
  receiptUrl: z.string().nullable(),
});
export type MemberPayment = z.infer<typeof memberPaymentSchema>;

/** 승인 결과 — 결과를 모르면(토스 5xx·타임아웃) CHECKING 이고 웹훅이 최종 상태를 넣는다. */
export const confirmPaymentResultSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('DONE'), payment: memberPaymentSchema }),
  z.object({ outcome: z.literal('CHECKING'), paymentId: z.uuid() }),
]);
export type ConfirmPaymentResult = z.infer<typeof confirmPaymentResultSchema>;

export const paymentStatusInputSchema = z.object({ paymentId: uuidSchema });

// ---------------------------------------------------------------------------
// 어드민 표면 (paymentManagement.*)
// ---------------------------------------------------------------------------

export const listAdminPaymentsInputSchema = paginationSchema.extend({
  /** 비우면 DEFAULT_ADMIN_PAYMENT_STATUSES(READY 제외). */
  statuses: z.array(z.enum(PAYMENT_STATUSES)).min(1).max(PAYMENT_STATUSES.length).optional(),
  provider: paymentProviderSchema.optional(),
});
export type ListAdminPaymentsInput = z.infer<typeof listAdminPaymentsInputSchema>;

export const adminPaymentRowSchema = z.object({
  id: z.uuid(),
  orderId: z.string(),
  userEmail: z.string(),
  provider: paymentProviderSchema,
  status: z.enum(PAYMENT_STATUSES),
  amount: z.number().int(),
  balanceAmount: z.number().int(),
  method: z.string().nullable(),
  approvedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type AdminPaymentRow = z.infer<typeof adminPaymentRowSchema>;

export const adminPaymentListSchema = paginatedSchema(adminPaymentRowSchema);
export type AdminPaymentList = z.infer<typeof adminPaymentListSchema>;

export const adminPaymentCancelSchema = z.object({
  id: z.uuid(),
  amount: z.number().int(),
  reason: z.string(),
  status: z.enum(PAYMENT_CANCEL_STATUSES),
  /** 상점관리자 직접 취소는 null. */
  requestedByEmail: z.string().nullable(),
  transactionKey: z.string().nullable(),
  canceledAt: z.iso.datetime().nullable(),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type AdminPaymentCancel = z.infer<typeof adminPaymentCancelSchema>;

export const adminPaymentDetailSchema = adminPaymentRowSchema.extend({
  orderName: z.string(),
  paymentKey: z.string().nullable(),
  receiptUrl: z.string().nullable(),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  cancels: z.array(adminPaymentCancelSchema),
});
export type AdminPaymentDetail = z.infer<typeof adminPaymentDetailSchema>;

export const cancelPaymentInputSchema = z.object({
  paymentId: uuidSchema,
  amount: wonSchema,
  reason: z
    .string()
    .trim()
    .min(1, '환불 사유를 입력해 주세요.')
    .max(MAX_CANCEL_REASON_LENGTH, `환불 사유는 ${MAX_CANCEL_REASON_LENGTH}자 이하여야 합니다.`),
});
export type CancelPaymentInput = z.infer<typeof cancelPaymentInputSchema>;

export const retryPaymentCancelInputSchema = z.object({ cancelId: uuidSchema });
export type RetryPaymentCancelInput = z.infer<typeof retryPaymentCancelInputSchema>;
